import type { UserId } from "@core/domains/identity";
import { singleton } from "@core/foundation/class";
import { getBrandedString } from "@core/foundation/string";
import { hasValue } from "@core/foundation/types";
import type { PK, SK } from "@core/ports/json-storage";
import type { ZodUnwrap, z } from "@core/zod";

import { BaseDeviceRepository } from "@react/ionic/storage";

import type { GridEpochId } from "@apis/calorie-grid/models";
import { LogEntry } from "@apis/calorie-grid/models";

import { deviceStorage } from "../bootstrap/device-storage.ts";
import { TABLE_NAME } from "../bootstrap/storage.ts";

const identifierFields = { gridEpochId: true, id: true, userId: true } as const;

const LogEntryIdentifierSchema = LogEntry.schema.pick(identifierFields);

type LogEntryIdentifier = z.infer<typeof LogEntryIdentifierSchema>;

const LogEntryDataSchema = LogEntry.schema.omit(identifierFields);

class LogEntryRepository extends BaseDeviceRepository<
  ZodUnwrap<typeof LogEntryIdentifierSchema>,
  ZodUnwrap<typeof LogEntryDataSchema>,
  LogEntry,
  "log-entry"
> {
  public static readonly getInstance = singleton(
    () => new LogEntryRepository(),
  );

  private constructor() {
    super({
      dataSchema: LogEntryDataSchema,
      deviceStorage,
      entityType: "log-entry",
      identifierSchema: LogEntryIdentifierSchema,
      readSchema: LogEntry.readSchema,
      resourceName: "Log entry",
      tableName: TABLE_NAME,
    });
  }

  public async getAllByEpoch(
    userId: UserId,
    gridEpochId: GridEpochId,
  ): Promise<readonly LogEntry[]> {
    const PK = this.getPK({ gridEpochId, userId });

    // @mutable
    const logEntries: LogEntry[] = [];

    let cursor: string | undefined;

    do {
      const page = await this.db.getByPK(PK, { cursor });
      logEntries.push(...page.items);
      cursor = page.nextCursor;
    } while (hasValue(cursor));

    return logEntries.toSorted((left, right) =>
      left.orderKey.localeCompare(right.orderKey),
    );
  }

  protected getPK({
    gridEpochId,
    userId,
  }: Pick<LogEntryIdentifier, "gridEpochId" | "userId">): PK<
    typeof this.entityType
  > {
    return getBrandedString(`${this.entityType}#${userId}#${gridEpochId}`);
  }

  protected getSK({ id }: Pick<LogEntryIdentifier, "id">): SK {
    return getBrandedString(id);
  }
}

export const logEntryRepository = LogEntryRepository.getInstance();
